import React, { useState } from 'react';
import axios from 'axios';

const CalendarCard = ({ events, setEvents, tasks, user, adminUsers, API_BASE }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    event_time: '',
    end_time: ''
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Create array of days
  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const hasEvent = (day) => {
    if (!day) return false;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.some(event => event.event_date?.substring(0, 10) === dateStr) ||
           tasks.some(task => task.task_date?.substring(0, 10) === dateStr);
  };

  // Get upcoming events (next 3)
  const upcomingEvents = events
    .filter(event => new Date(event.event_date) >= new Date().setHours(0, 0, 0, 0))
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
    .slice(0, 3);

  const formatEventTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getEventColor = (index) => {
    const colors = ['purple', 'blue', 'green'];
    return colors[index % colors.length];
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title.trim()) {
      alert('Event title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/calendar/events`,
        eventForm,
        { withCredentials: true }
      );
      setEvents([...events, response.data]);
      setEventForm({
        title: '',
        description: '',
        event_date: new Date().toISOString().split('T')[0],
        event_time: '',
        end_time: ''
      });
      setShowEventModal(false);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    }
  };

  return (
    <div className="desk-card calendar-card">
      <div className="desk-card-header">
        <div className="desk-card-header-left">
          <div className="desk-card-icon calendar">
            <span>&#128197;</span>
          </div>
          <span className="desk-card-title">Calendar</span>
        </div>
        <button className="desk-card-action" onClick={() => setShowEventModal(true)}>
          + Add Event
        </button>
      </div>
      <div className="desk-card-body">
        {/* Mini Calendar */}
        <div className="mini-calendar">
          <div className="mini-calendar-header">
            <span>{monthNames[month]} {year}</span>
            <div className="mini-calendar-nav">
              <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>&#8249;</button>
              <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>&#8250;</button>
            </div>
          </div>
          <div className="mini-calendar-grid">
            {dayNames.map((day, i) => (
              <div key={i} className="cal-day-name">{day}</div>
            ))}
            {days.map((day, index) => (
              <div
                key={index}
                className={`cal-day ${day ? '' : 'empty'} ${isToday(day) ? 'today' : ''} ${hasEvent(day) ? 'has-event' : ''}`}
              >
                {day}
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="upcoming-events">
          <div className="upcoming-events-title">Upcoming</div>
          {upcomingEvents.length === 0 ? (
            <div style={{ fontSize: '0.8rem', color: '#a0aec0', textAlign: 'center', padding: '0.5rem' }}>
              No upcoming events
            </div>
          ) : (
            upcomingEvents.map((event, index) => (
              <div key={event.id} className={`event-item ${getEventColor(index)}`}>
                <div className={`event-indicator ${getEventColor(index)}`}></div>
                <div className="event-info">
                  <div className="event-title">{event.title}</div>
                  <div className="event-time">
                    {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {event.event_time && ` at ${formatEventTime(event.event_time)}`}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Event Creation Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Event</h3>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Enter event title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Enter event description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Event Date</label>
                <input
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={eventForm.event_time}
                    onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={eventForm.end_time}
                    onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEventModal(false)}>Cancel</button>
              <button className="btn-create" onClick={handleCreateEvent}>Create Event</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarCard;
